// src/services/demoApi.js
import axios from "axios";

const DEMO_API_URL =
  process.env.REACT_APP_DEMO_API_URL || "http://localhost:5001/api";

const demoApi = axios.create({
  baseURL: DEMO_API_URL,
  timeout: 30000,
});

// Unwrap axios envelope so callers get the JSON body directly,
// matching the behaviour of the live apiService interceptor.
demoApi.interceptors.response.use(
  (response) => response.data,
  (error) => Promise.reject(error),
);

demoApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("axoaccessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

demoApi.getCompanyScore = (companyId) =>
  demoApi.get(`/companies/${companyId}/score`);

// Demo users never trigger a real website snapshot, just fetch the score.
demoApi.refreshWebsiteHealth = (companyId) =>
  demoApi.get(`/companies/${companyId}/score`);

// Phase 5, items 2/3/5, not available in the demo, so return empty shapes
// rather than letting the demo UI issue 404s.
demoApi.getInboundReplies = async () => ({ replies: [] });
demoApi.getCompanyDossier = async () => ({ dossier: null });
demoApi.getLatestCompanyResearch = async () => ({ research: null });
demoApi.generateCompanyResearch = async () => ({ summaryText: "" });
demoApi.getChannelActions = async () => ({ actions: [] });
demoApi.recordChannelAction = async () => ({ success: true });
demoApi.sendChannelAction = async () => ({ success: true });

// Phase 5A, Daily briefing / Sales Workspace / AI Cost Transparency. The
// demo backend doesn't run the real (spend-incurring) AI endpoints, so these
// resolve to empty shells: the UI shows its empty states instead of 404ing
// or accidentally triggering real AI spend.
demoApi.getTodayBriefing = async () => ({ briefing: null });
demoApi.refreshBriefing = async () => ({ cached: false, requiresGeneration: true, briefing: null });
demoApi.estimateBriefing = async () => ({ estimate: null });
demoApi.rejectBriefingEstimate = async () => ({ success: true });
demoApi.generateBriefing = async () => ({ briefing: null });
demoApi.estimateOutreachDraft = async () => ({
  estimate: { logId: null, estimatedCostGbp: 0 },
});
demoApi.rejectOutreachEstimate = async () => ({ success: true });
demoApi.prepareOutreachDraft = async () => ({});
demoApi.getOutreachDrafts = async () => ({ drafts: [], contact: null });
demoApi.updateOutreachDraft = async () => ({ success: true });
demoApi.approveAndSendOutreach = async () => ({ success: true });
demoApi.bulkApproveAndSendOutreach = async (draftIds = []) => ({
  success: true,
  summary: {
    totalRequested: draftIds.length,
    sent: draftIds.length,
    failed: 0,
    results: draftIds.map((draftId) => ({ draftId, success: true })),
  },
});
demoApi.rejectOutreach = async () => ({ success: true });
demoApi.estimateCompanyResearch = async () => ({
  estimate: { logId: null, estimatedCostGbp: 0 },
});
demoApi.rejectCompanyResearchEstimate = async () => ({ success: true });
demoApi.recordRevenueOutcome = async () => ({ success: true });
demoApi.triggerEnrichment = async () => ({ success: true });
demoApi.refreshCompanyFinancials = async () => ({ success: true, data: null });

// Phase 5B/5C, pipeline board, AI performance and revenue forecast.
demoApi.getPipelineBoard = async () => ({ board: [] });
demoApi.movePipelineStage = async () => ({ success: true });
demoApi.getAiPerformanceDashboard = async () => ({ dashboard: null });
demoApi.getAiPerformanceTimeseries = async () => ({ timeseries: null });
demoApi.getStageTimings = async () => ({ timings: null });
demoApi.getOutreachPerformance = async () => ({ performance: null });
demoApi.getInsights = async () => ({ insights: null });
demoApi.getForecast = async () => ({ forecast: null });

// ── Notification centre ────────────────────────────────────────────────────
// The demo backend does not persist notifications, so the demo environment
// gets a realistic, in-memory feed. Read state lives in this module for the
// life of the tab, which is enough to exercise the page (filters, counts,
// detail dialog, mark-read) without touching the live tables.
const DEMO_NOTIFICATION_CATEGORIES = [
  "brief",
  "email",
  "reply",
  "website",
  "qa",
  "payment",
  "outreach",
  "activity",
  "user",
  "system",
];
const DEMO_NOTIFICATION_SEVERITIES = [
  "info",
  "success",
  "warning",
  "error",
];

const hoursAgo = (hours) =>
  new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

const DEMO_CUSTOMER = {
  id: "demo-customer",
  name: "Demo Workspace",
  planId: "axo_ai",
};

const DEMO_NOTIFICATIONS = [
  {
    id: "demo-notif-brief",
    customerId: DEMO_CUSTOMER.id,
    direction: "inbound",
    channel: "system",
    category: "brief",
    severity: "success",
    type: "brief.submitted",
    title: "New brief submitted: Northwind Joinery",
    message:
      "Alex Morgan submitted the brief for Northwind Joinery. The website build has started automatically.",
    recipient: "alex@northwind-joinery.co.uk",
    status: "received",
    actorName: "Alex Morgan",
    entityType: "brief_form_submission",
    entityId: "demo-submission-1",
    link: "/dashboard",
    metadata: {
      companyId: "demo-company-1",
      contactName: "Alex Morgan",
      contactEmail: "alex@northwind-joinery.co.uk",
    },
    createdAt: hoursAgo(2),
  },
  {
    id: "demo-notif-confirmation",
    customerId: DEMO_CUSTOMER.id,
    direction: "outbound",
    channel: "email",
    category: "email",
    severity: "success",
    type: "brief.confirmation.email",
    title: "Brief confirmation sent: Northwind Joinery",
    message: "Confirmation emailed to alex@northwind-joinery.co.uk.",
    recipient: "alex@northwind-joinery.co.uk",
    status: "sent",
    actorName: "AXO Signal",
    entityType: "brief_form_submission",
    entityId: "demo-submission-1",
    link: "/dashboard",
    metadata: { companyId: "demo-company-1" },
    createdAt: hoursAgo(2),
  },
  {
    id: "demo-notif-website",
    customerId: DEMO_CUSTOMER.id,
    direction: "system",
    channel: "system",
    category: "website",
    severity: "info",
    type: "website.qa_pending",
    title: "Website build complete: Northwind Joinery",
    message:
      "The build finished and is now in internal quality review.",
    recipient: "Workspace users",
    status: "recorded",
    actorName: "AXO Signal",
    entityType: "customer_company",
    entityId: "demo-cc-1",
    link: "/dashboard",
    metadata: {
      companyId: "demo-company-1",
      previewUrl: "https://preview.example.com/northwind",
    },
    createdAt: hoursAgo(5),
  },
  {
    id: "demo-notif-reply",
    customerId: DEMO_CUSTOMER.id,
    direction: "inbound",
    channel: "email",
    category: "reply",
    severity: "success",
    type: "reply.received",
    title: "New reply from Sam Taylor",
    message: "Re: A quick idea for Taylor & Co — classified as positive.",
    recipient: "sam@taylorco.example",
    status: "received",
    actorName: "Sam Taylor",
    entityType: "inbound_message",
    entityId: "demo-reply-1",
    link: "/dashboard?section=revenue-intel&ri_tab=agent",
    metadata: {
      classification: "positive",
      companyId: "demo-company-2",
    },
    createdAt: hoursAgo(8),
  },
  {
    id: "demo-notif-email",
    customerId: DEMO_CUSTOMER.id,
    direction: "outbound",
    channel: "email",
    category: "email",
    severity: "success",
    type: "email.outreach_draft",
    title: "Outreach email sent: A quick idea for Taylor & Co",
    message: "AI-assisted email sent to sam@taylorco.example.",
    recipient: "sam@taylorco.example",
    status: "sent",
    actorName: "Jordan Blake",
    entityType: "outreach_draft",
    entityId: "demo-draft-1",
    link: "/dashboard?section=revenue-intel&ri_tab=agent",
    metadata: {
      companyId: "demo-company-2",
      draftType: "outreach_email",
    },
    createdAt: hoursAgo(9),
  },
  {
    id: "demo-notif-activity",
    customerId: DEMO_CUSTOMER.id,
    direction: "system",
    channel: "system",
    category: "activity",
    severity: "info",
    type: "activity.pipeline",
    title: "Pipeline stage moved by Jordan Blake",
    message: "Jordan Blake moved a pipeline stage in your workspace.",
    recipient: "Workspace users",
    status: "recorded",
    actorName: "Jordan Blake",
    entityType: "api_request",
    link: "/dashboard?section=revenue-intel&ri_tab=pipeline",
    metadata: {
      method: "POST",
      path: "/api/pipeline/demo-cc-2/stage",
      statusCode: 200,
    },
    createdAt: hoursAgo(12),
  },
  {
    id: "demo-notif-payment",
    customerId: DEMO_CUSTOMER.id,
    direction: "system",
    channel: "system",
    category: "payment",
    severity: "success",
    type: "payment.received",
    title: "Payment received: AXO-DEMO-123",
    message: "Payment confirmed for companyId=demo-company-3.",
    recipient: "Workspace users",
    status: "recorded",
    actorName: "AXO Signal",
    entityType: "payment",
    entityId: "demo-payment-1",
    link: "/dashboard",
    metadata: {
      companyId: "demo-company-3",
      amount: 4999,
      currency: "GBP",
      reference: "AXO-DEMO-123",
    },
    createdAt: hoursAgo(26),
  },
  {
    id: "demo-notif-qa",
    customerId: DEMO_CUSTOMER.id,
    direction: "system",
    channel: "system",
    category: "qa",
    severity: "success",
    type: "qa.approved",
    title: "Website approved: Northwind Joinery",
    message:
      "Your website has passed quality checks and the payment request has been prepared.",
    recipient: "Workspace users",
    status: "recorded",
    actorName: "Paras Mistry",
    entityType: "customer_company",
    entityId: "demo-cc-1",
    link: "/dashboard",
    metadata: { companyId: "demo-company-1" },
    createdAt: hoursAgo(30),
  },
  {
    id: "demo-notif-user",
    customerId: DEMO_CUSTOMER.id,
    direction: "system",
    channel: "system",
    category: "user",
    severity: "success",
    type: "user.invited",
    title: "User invited: Priya Shah",
    message: "Performed by Jordan Blake.",
    recipient: "Workspace users",
    status: "recorded",
    actorName: "Jordan Blake",
    entityType: "admin_audit_log",
    entityId: "demo-audit-1",
    link: "/team",
    metadata: { action: "user.invited" },
    createdAt: hoursAgo(48),
  },
  {
    id: "demo-notif-announcement",
    customerId: DEMO_CUSTOMER.id,
    direction: "system",
    channel: "system",
    category: "system",
    severity: "info",
    type: "system.announcement",
    title: "New pipeline forecasting is live",
    message:
      "Forecast is now available in the Sales workspace for AI plans.",
    recipient: "Workspace users",
    status: "recorded",
    actorName: "AXO Signal",
    link: "/dashboard?section=revenue-intel&ri_tab=forecast",
    metadata: { announcement: true },
    createdAt: hoursAgo(72),
  },
].map((notification) => ({
  ...notification,
  updatedAt: notification.createdAt,
  customer: DEMO_CUSTOMER,
  readAt: null,
  requiredCapability: null,
}));

const demoReadNotificationIds = new Set(["demo-notif-user"]);

function demoNotificationRows() {
  return DEMO_NOTIFICATIONS.map((notification) => ({
    ...notification,
    readAt: demoReadNotificationIds.has(notification.id)
      ? notification.createdAt
      : null,
  }));
}

function filterDemoNotifications(params = {}) {
  const page = Math.max(1, parseInt(params.page, 10) || 1);
  const limit = Math.min(100, Math.max(1, parseInt(params.limit, 10) || 25));
  const search = String(params.q || "").trim().toLowerCase();

  const all = demoNotificationRows();
  const scoped = all.filter((notification) => {
    if (params.direction && notification.direction !== params.direction) {
      return false;
    }
    if (params.category && notification.category !== params.category) {
      return false;
    }
    if (params.severity && notification.severity !== params.severity) {
      return false;
    }
    if (params.unread === "true" && notification.readAt) return false;
    if (search) {
      const haystack = [
        notification.title,
        notification.message,
        notification.recipient,
        notification.actorName,
        notification.type,
        notification.category,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });

  const start = (page - 1) * limit;
  const data = scoped.slice(start, start + limit);
  const byCategory = {};
  all.forEach((notification) => {
    byCategory[notification.category] =
      (byCategory[notification.category] || 0) + 1;
  });

  return {
    success: true,
    data,
    pagination: {
      total: scoped.length,
      page,
      limit,
      totalPages: Math.max(1, Math.ceil(scoped.length / limit)),
    },
    counts: {
      total: all.length,
      unread: all.filter((notification) => !notification.readAt).length,
      inbound: all.filter((n) => n.direction === "inbound").length,
      outbound: all.filter((n) => n.direction === "outbound").length,
      system: all.filter((n) => n.direction === "system").length,
      byCategory,
    },
    categories: DEMO_NOTIFICATION_CATEGORIES,
    severities: DEMO_NOTIFICATION_SEVERITIES,
  };
}

demoApi.getNotifications = async (params = {}) =>
  filterDemoNotifications(params);

demoApi.getNotificationUnreadCount = async () => ({
  success: true,
  count: demoNotificationRows().filter((n) => !n.readAt).length,
});

demoApi.markNotificationRead = async (id) => {
  demoReadNotificationIds.add(id);
  return { success: true };
};

demoApi.markAllNotificationsRead = async () => {
  DEMO_NOTIFICATIONS.forEach((notification) => {
    demoReadNotificationIds.add(notification.id);
  });
  return { success: true, updated: DEMO_NOTIFICATIONS.length };
};

demoApi.createNotificationAnnouncement = async (payload = {}) => ({
  success: true,
  count: 1,
  data: [
    {
      id: `demo-announcement-${Date.now()}`,
      ...payload,
      customer: DEMO_CUSTOMER,
      createdAt: new Date().toISOString(),
    },
  ],
});

export default demoApi;
